import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useRecoilState } from 'recoil';

import {
  createNewTopic,
  getAllMessagesToOneUser,
  getUserChatTopicListWithFilter,
} from '@app/adapter/chat-service';
import { loggedInUserState } from '@app/domain/app';
import { totalUnreadCountState } from '@app/domain/chat';
import { organization } from '@app/domain/organization';
import { Message, MessageCategory, MessageReadStatus } from '@app/types/chats';
import { isError } from '@app/utils/error';
import { useSetSnackbar } from '@app/utils/useSetSnackbar';

/** 運営が作成したTopicの表示名(相手側にも表示されるため個人名は使わない) */
export const ADMIN_TOPIC_NAME = '事務局';

/** 未読数の再取得間隔(ミリ秒) */
const UNREAD_COUNT_POLLING_INTERVAL = 60000;

/**
 * 対象ユーザーとのチャット詳細画面を開くフック
 * Topicが既に存在すればそのTopicへ、なければTopicを作成した上で遷移する
 */
export function useOpenChatWithUser() {
  const navigate = useNavigate();
  const setSnackbar = useSetSnackbar();
  const loggedInUser = useRecoilValue(loggedInUserState);
  const organizationState = useRecoilValue(organization);
  const [isOpeningChat, setIsOpeningChat] = useState(false);

  const openChatWithUser = useCallback(
    async (userId?: string) => {
      if (!loggedInUser?.id || !userId) return;
      setIsOpeningChat(true);
      try {
        const {
          data: { value: topics },
        } = await getUserChatTopicListWithFilter(loggedInUser.id, {
          filter: { ownerIds: [userId] },
          pageSize: 1,
        });
        const topic =
          topics[0] ||
          (
            await createNewTopic(
              loggedInUser.id,
              userId,
              // NOTE:運営は組織に属さずorganizationState.nameが空になるためフォールバックする
              organizationState.name || ADMIN_TOPIC_NAME
            )
          ).data;
        navigate(`/chats/${topic.id}`);
      } catch (e) {
        if (isError(e)) {
          console.error('openChatWithUser', e.message);
        }
        setSnackbar(true, 'チャットの取得に失敗しました', 'error');
      } finally {
        setIsOpeningChat(false);
      }
    },
    [loggedInUser, organizationState.name, navigate, setSnackbar]
  );

  return { isOpeningChat, openChatWithUser };
}

/**
 * ログインユーザー宛の未読メッセージ数を保持するフック
 * 一定間隔でポーリングし、AppContext経由で各画面へ提供する
 */
export function useChatUnreadCount() {
  const loggedInUser = useRecoilValue(loggedInUserState);
  const [unreadCount, setUnreadCount] = useRecoilState(totalUnreadCountState);

  const fetchMessagesAndUnreadCount = useCallback(async () => {
    if (!loggedInUser?.id) {
      return;
    }
    try {
      const result = await getAllMessagesToOneUser(loggedInUser.id);
      // NOTE:categoryは送信元アプリを表す。本アプリ(supply)からの送信は'supply'となるため、
      //      ドクター(demand)からの受信メッセージのみが未読対象となる。
      //      運営・医療機関ユーザー同士のチャットに対応する際は要見直し。
      const unreadMessagesCount = result.data.value.filter(
        (message: Message) =>
          message.readStatus === MessageReadStatus.UNREAD &&
          message.category === MessageCategory.DEMAND
      ).length;
      setUnreadCount(unreadMessagesCount);
    } catch (error) {
      if (isError(error)) {
        console.error(`Messageの取得に失敗しました, ${error.message}`);
      } else {
        console.error(`Messageの取得に失敗しました`);
      }
    }
  }, [loggedInUser?.id, setUnreadCount]);

  useEffect(() => {
    void fetchMessagesAndUnreadCount();
    const intervalId = setInterval(() => {
      void fetchMessagesAndUnreadCount();
    }, UNREAD_COUNT_POLLING_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchMessagesAndUnreadCount]);

  return useMemo(
    () => ({ fetchMessagesAndUnreadCount, unreadCount }),
    [fetchMessagesAndUnreadCount, unreadCount]
  );
}
